import { Inertia } from '@inertiajs/inertia'
import React, { useState, useEffect } from 'react'
import { hasPermission } from '../../utils/Permissions';
import Layout from '../../Shared/Layout/LayoutAdmin';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { Link, usePage } from '@inertiajs/inertia-react'
import AlertDismissible from '../../Shared/Components/AlertDismissible';
import PaginationList from '../../Shared/Components/PaginationList';
import Breadcrumbs from '../../Shared/Components/Breadcrumbs';

const List = ({ items, filters, emitters, advisers }) => {
    const { flash } = usePage().props;

    const [dateFrom, setDateFrom] = useState(filters.date_from || '');
    const [dateTo, setDateTo] = useState(filters.date_to || '');
    const [emitter, setEmitter] = useState(filters.emitter || '');
    const [contractor, setContractor] = useState(filters.contractor || '');
    const [policy, setPolicy] = useState(filters.policy || '');
    const [folio, setFolio] = useState(filters.folio || '');
    const [adviser, setAdviser] = useState(filters.adviser || '');

    const adviserOptions = React.useMemo(() => {
        if (!advisers) return [];
        if (Array.isArray(advisers)) return advisers;
        if (typeof advisers === 'object')
            return Object.entries(advisers).map(([id, text]) => ({ id, text }));
        return [];
    }, [advisers]);

    useEffect(() => {
        setDateFrom(filters.date_from || '');
        setDateTo(filters.date_to || '');
        setEmitter(filters.emitter || '');
        setContractor(filters.contractor || '');
        setPolicy(filters.policy || '');
        setFolio(filters.folio || '');
        setAdviser(filters.adviser || '');
    }, [filters]);

    function doSearch(e) {
        e && e.preventDefault();
        Inertia.get(route('commissions.inbursa.list'), {
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            emitter: emitter || undefined,
            folio: folio || undefined,
            adviser: adviser || undefined,
            contractor: contractor || undefined,
            policy: policy || undefined,
        }, {
            preserveState: true,
            preserveScroll: true,
            replace: true,
        });
    }

    function clearFilters() {
        setDateFrom('');
        setDateTo('');
        setEmitter('');
        setFolio('');
        setAdviser('');
        setContractor('');
        setPolicy('');
        Inertia.get(route('commissions.inbursa.list'), {}, {
            preserveState: false,
            replace: true,
        });
    }

    const formatDate = d => {
        if (!d) return '';
        let dateObj = d instanceof Date ? d : null;
        if (!dateObj) {
            let str = String(d).trim().replace(/T.*$/, '');
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
                const [y, m, day] = str.split('-').map(n => parseInt(n, 10));
                dateObj = new Date(Date.UTC(y, m - 1, day));
            } else {
                const t = Date.parse(str);
                if (!isNaN(t)) dateObj = new Date(t);
            }
        }
        if (!dateObj || isNaN(dateObj.getTime())) return '';
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        const month = dateObj.toLocaleString('en-US', { month: 'long', timeZone: 'UTC' });
        const year = dateObj.getUTCFullYear();
        return `${day} ${month} ${year}`; // Formato: 04 September 2025
    };

    const formatMoney = n => {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(Number(n || 0));
    };

    return (
        <Row>
            <div className="mb-7 mt-7">
                <Breadcrumbs currentPath="/admin/commissions/inbursa/list" />
            </div>

            <Col className="mb-2 px-1">
                <h2>Comisiones Inbursa</h2>
            </Col>

            {flash?.success && <AlertDismissible message={flash.success} type="success" />}
            {flash?.error && <AlertDismissible message={flash.error} type="danger" />}

            {/* Filtros */}
            <Form onSubmit={doSearch} className="mb-4 px-1">
                <div className="d-flex flex-wrap gap-2 align-items-end">
                    {/* fechas */}
                    <div>
                        <Form.Label className='mb-1'>Desde</Form.Label>
                        <Form.Control type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    </div>
                    <div>
                        <Form.Label className='mb-1'>Hasta</Form.Label>
                        <Form.Control type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                    </div>

                    {/* Emisor como <select> id = name, texto = description */}
                    <div>
                        <Form.Label className='mb-1'>Emisor</Form.Label>
                        <Form.Select value={emitter} onChange={e => setEmitter(e.target.value)}>
                            <option value=''>Todos</option>
                            {emitters && emitters.map(e => (
                                <option key={e.id} value={e.id}>{e.text}</option>
                            ))}
                        </Form.Select>
                    </div>

                    <div>
                        <Form.Label className='mb-1'>Contratante</Form.Label>
                        <Form.Control value={contractor} onChange={e => setContractor(e.target.value)} placeholder="Nombre" />
                    </div>
                    <div>
                        <Form.Label className='mb-1'>Póliza</Form.Label>
                        <Form.Control value={policy} onChange={e => setPolicy(e.target.value)} placeholder="Póliza" />
                    </div>
                    <div>
                        <Form.Label className='mb-1'>Folio</Form.Label>
                        <Form.Control value={folio} onChange={e => setFolio(e.target.value)} placeholder="Folio" />
                    </div>
                    <div>
                        <Form.Label className='mb-1'>Asesor</Form.Label>
                        <Form.Control value={adviser} onChange={e => setAdviser(e.target.value)} placeholder="Nombre asesor" />
                    </div>
                    <div className="d-flex gap-2">
                        <Button type="submit" variant="primary" className='btn-sm'>Filtrar</Button>
                        <Button type="button" variant="secondary" className='btn-sm' onClick={clearFilters}>Limpiar</Button>
                    </div>
                </div>
            </Form>
            <div className="d-flex justify-content-end gap-2 mt-2 mb-2 w-100">
                <Link href={route('commissions.inbursa.list_missing')} className='btn btn-sm btn-primary'>Polizas Faltantes</Link>
                {hasPermission('quotes.commissions.add') && (
                    <Link href={route('commissions.inbursa.import')} className='btn btn-sm btn-success'>Importar Excel</Link>
                )}
            </div>
            {/* Tabla */}
            <div className="card card-flush h-md-100">
                <div className="card-body pt-6 px-2">
                    <div className="table-responsive mb-4">
                        <table className="table table-row-dashed align-middle gs-0 gy-3 my-0">
                            <thead>
                                <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                    <th className='p-0 pb-3 text-start min-w-140px'>Fecha</th>
                                    <th className='p-0 pb-3 text-start min-w-100px'>Folio</th>
                                    <th className='p-0 pb-3 text-start min-w-120px'>Póliza</th>
                                    <th className='p-0 pb-3 text-start min-w-200px'>Contratante</th>
                                    <th className='p-0 pb-3 text-start min-w-160px'>Emisor</th>
                                    <th className='p-0 pb-3 text-start min-w-120px'>Asesor</th>
                                    <th className='p-0 pb-3 text-end min-w-120px'>Prima</th>
                                    <th className='p-0 pb-3 text-end min-w-120px'>Comisión</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.data.length === 0 && (
                                    <tr>
                                        <td className="px-6 py-3 text-center" colSpan="8">
                                            No hay datos.
                                        </td>
                                    </tr>
                                )}
                                {items.data.map(row => (
                                    <tr key={row.id}>
                                        <td>{formatDate(row.date)}</td>
                                        <td>{row.folio}</td>
                                        <td>{row.policy}</td>
                                        <td className="text-truncate" style={{ maxWidth: 220 }}>{row.contractor}</td>
                                        <td>{row.emitter?.description || row.emitter}</td>
                                        <td>{row.adviser?.user?.name || row.adviser_name || row.origin_agent}</td>
                                        <td className="text-end">{formatMoney(row.total_bonus)}</td>
                                        <td className="text-end">{formatMoney(row.commission_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <PaginationList links={items.links} />
                </div>
            </div>
        </Row >
    );
}

List.layout = page => <Layout children={page} title="Comisiones Inbursa" />

export default List
